// Auto-scroll-to-bottom for the agentic transcript (see `../useAgenticRunController.js`):
// track whether the user is scrolled to the bottom, jump there on new content unless they've
// scrolled up to read back, and keep pinned to the bottom while the last item's height grows via
// `ResizeObserver` (e.g. the live turn's reasoning/steps/final-answer streaming in).
// `AIAssistantChatPanel.jsx` drives this off `useAgenticRunController`'s `history` + live-turn
// fields.
import {useCallback, useEffect, useRef, useState} from 'react';

const AUTO_SCROLL_THRESHOLD = 64;

/**
 * Transcript metrics that drive `useAgenticScrollToBottom`. Chip/picker UI must be
 * counted so opening a predefined-prompt picker scrolls the same way a typed submit does.
 *
 * @param {{
 *   conversationId?: string|null,
 *   historyLength?: number,
 *   hasActiveTurn?: boolean,
 *   goal?: string|null,
 *   lastHistoryId?: string|null,
 *   pickerOpen?: boolean,
 * }} args
 * @returns {{conversationKey: string|null, itemCount: number, lastItemKey: string|null}}
 */
export function buildAgenticScrollMetrics({
    conversationId = null,
    historyLength = 0,
    hasActiveTurn = false,
    goal = null,
    lastHistoryId = null,
    pickerOpen = false,
} = {}) {
    // After a completed turn `goal` stays set (`hasActiveTurn`), but opening the
    // Trace picker still appends UI below that turn. Count it as the latest item
    // so chip-open scrolls the same way a typed submit does.
    const pickerVisible = Boolean(pickerOpen);
    return {
        conversationKey: conversationId ?? null,
        itemCount: historyLength + (hasActiveTurn ? 1 : 0) + (pickerVisible ? 1 : 0),
        lastItemKey: pickerVisible
            ? 'trace-picker'
            : (hasActiveTurn ? `live-${goal}` : (lastHistoryId || null)),
    };
}

/**
 * @param {{conversationKey: string|null, itemCount: number, lastItemKey: string|null}} args
 * @returns {{scrollerRef: React.RefObject, lastItemRef: React.RefObject, handleScroll: Function}}
 */
export default function useAgenticScrollToBottom({conversationKey, itemCount, lastItemKey}) {
    const scrollerRef = useRef(null);
    const lastItemRef = useRef(null);
    const isInitialMountRef = useRef(true);
    const previousRef = useRef({conversationKey: null, count: 0, lastItemKey: null});
    const [isAtBottom, setIsAtBottom] = useState(true);

    const scrollToBottom = useCallback(() => {
        const element = scrollerRef.current;
        if (!element) {
            return;
        }
        const jump = () => {
            element.scrollTop = element.scrollHeight;
        };
        jump();
        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => requestAnimationFrame(jump));
        }
    }, []);

    const handleScroll = useCallback(() => {
        const element = scrollerRef.current;
        if (!element) {
            return;
        }
        const atBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < AUTO_SCROLL_THRESHOLD;
        setIsAtBottom(atBottom);
    }, []);

    // Thread switch (or a brand-new/empty thread) resets scroll tracking so the next content jumps
    // to the bottom rather than inheriting a stale "user scrolled up" state from a previous thread.
    useEffect(() => {
        isInitialMountRef.current = true;
        setIsAtBottom(true);
        previousRef.current = {conversationKey, count: 0, lastItemKey: null};
    }, [conversationKey]);

    useEffect(() => {
        const previous = previousRef.current;
        const isNewItem = Boolean(
            previous.conversationKey === conversationKey
            && itemCount > previous.count
        );
        const lastItemChanged = Boolean(
            previous.conversationKey === conversationKey
            && lastItemKey
            && lastItemKey !== previous.lastItemKey
            && (
                previous.lastItemKey === 'trace-picker'
                || lastItemKey === 'trace-picker'
                || String(lastItemKey).startsWith('live-')
            )
        );

        if (itemCount === 0) {
            isInitialMountRef.current = true;
            setIsAtBottom(true);
            previousRef.current = {conversationKey, count: 0, lastItemKey: null};
            return;
        }

        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            scrollToBottom();
        } else if (isNewItem || lastItemChanged) {
            scrollToBottom();
        } else if (isAtBottom) {
            scrollToBottom();
        }

        previousRef.current = {conversationKey, count: itemCount, lastItemKey};
    }, [conversationKey, itemCount, lastItemKey, isAtBottom, scrollToBottom]);

    useEffect(() => {
        const element = scrollerRef.current;
        const lastItemElement = lastItemRef.current;
        if (!element || !lastItemElement || typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const resizeObserver = new ResizeObserver(() => {
            if (isAtBottom) {
                element.scrollTop = element.scrollHeight;
            }
        });

        resizeObserver.observe(lastItemElement);
        resizeObserver.observe(element);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isAtBottom, itemCount, lastItemKey]);

    return {scrollerRef, lastItemRef, handleScroll};
}
